import React from 'react';
import { ElementGroupNames } from '../../../../common/src/enums/ElementGroupNames';
import {
    DataPanelRow,
    PanelBody,
    PanelButtonLink,
    PanelHeading,
    PanelResultHeader,
    PanelWithoutForm,
    TextWithLineBreaks,
} from '../../components/elements';
import { AssetsViewer } from '../../components/Surveys/Common/AssetsViewer';
import { useReactAsync } from '../../components/utils/useReactAsync';
import { useTranslate } from '../../mixins/translate/translate';
import { openApiFetchGet } from '../../store/openApiFetch';
import { SelectedOptionToText } from '../verificatieBerekening/SelectedOptionToText';
import {
    BreakDownOpenState,
    SurveyDetailsElementGroupReportPanel,
    SurveyDetailsElementPanel,
    SurveyDetailsManifestationPanel,
    SurveyDetailsUnitPanel,
    SurveyDetailsUnitPanelDetails,
} from '../verificatieBerekening/surveyDetailsElements';
import { Nen3140ScoreLabel } from './Nen3140ScoreLabel';
import { nen3140SelectOptions } from './nen3140SelectOptions';

async function loadElements({ surveyId }: { surveyId: string }) {
    const result = await openApiFetchGet('/api/surveys/{surveyId}/elements/nen3140/report', { surveyId });
    return result;
}

// eslint-disable-next-line @typescript-eslint/naming-convention
export function SurveyDetailsNen3140(props: { surveyId: string }) {
    const translate = useTranslate();
    const { data } = useReactAsync(loadElements, { surveyId: props.surveyId }, []);
    const [breakDownOpenState, setBreakDownOpenState] = React.useState<BreakDownOpenState>({});

    function expandAll() {
        const expandedBreakDownOpenState = {} as { [key: string]: boolean };
        expandedBreakDownOpenState[ElementGroupNames.Superstructure] = true;
        expandedBreakDownOpenState[ElementGroupNames.Substructure] = true;
        expandedBreakDownOpenState[ElementGroupNames.Foundation] = true;

        for (const element of data!) {
            expandedBreakDownOpenState[element.id] = true;
            for (const unit of element.units) {
                expandedBreakDownOpenState[unit.id] = true;
                for (const manifestation of unit.manifestations) {
                    expandedBreakDownOpenState[manifestation.id] = true;
                }
            }
        }
        setBreakDownOpenState(expandedBreakDownOpenState);
    }

    function collapseAll() {
        setBreakDownOpenState({});
    }

    function onChangeBreakdownOpenState(key: string) {
        breakDownOpenState[key] = !breakDownOpenState[key];
        setBreakDownOpenState({ ...breakDownOpenState });
    }

    if (!data) {
        return null;
    }

    return (
        <PanelWithoutForm id="Nen3140Report">
            <PanelHeading text={translate('nen3140.nen3140')} icon="fa fa-bolt"></PanelHeading>
            <PanelBody whiteBackground={true}>
                <PanelButtonLink
                    onClick={() => expandAll()}
                    text={translate('survey.expandAllComposition')}
                    icon="fa fa-plus-square-o"
                    xsmall={true}
                    rightAllign={true}
                />
                <PanelButtonLink
                    onClick={() => collapseAll()}
                    text={translate('survey.collapseAllComposition')}
                    icon="fa fa-minus-square-o "
                    xsmall={true}
                    rightAllign={true}
                />
                <SurveyDetailsElementGroupReportPanel extraColumns={['Beoordeling']}>
                    {data.map((element) => (
                        <SurveyDetailsElementPanel
                            // eslint-disable-next-line react/jsx-key
                            extraColumns={[<Nen3140ScoreLabel nen3140Score={element.nen3140Score} />]}
                            key={element.id}
                            breakDownOpenState={breakDownOpenState}
                            onChangeBreakdownOpenState={onChangeBreakdownOpenState}
                            element={element}
                        >
                            {element.units.map((unit) => (
                                <SurveyDetailsUnitPanel
                                    // eslint-disable-next-line react/jsx-key
                                    extraColumns={[<Nen3140ScoreLabel nen3140Score={unit.surveyInspectionStandardData.nen3140Score} />]}
                                    key={unit.id}
                                    breakDownOpenState={breakDownOpenState}
                                    onChangeBreakdownOpenState={onChangeBreakdownOpenState}
                                    unit={unit}
                                >
                                    <SurveyDetailsUnitPanelDetails>
                                        <PanelResultHeader>{translate('common.assessment')}</PanelResultHeader>
                                        <strong>
                                            <SelectedOptionToText
                                                selectedValue={unit.surveyInspectionStandardData.nen3140Score}
                                                options={nen3140SelectOptions.nen3140Score}
                                            />
                                        </strong>
                                        {unit.surveyInspectionStandardData.remarks && (
                                            <>
                                                <PanelResultHeader>{translate('common.remarks')}</PanelResultHeader>
                                                <TextWithLineBreaks text={unit.surveyInspectionStandardData.remarks} />
                                            </>
                                        )}
                                        {unit.assets.length > 0 && (
                                            <>
                                                <PanelResultHeader>{translate('nen3140.visualsAndDocuments')}</PanelResultHeader>
                                                <DataPanelRow>
                                                    <div className="p10">
                                                        <AssetsViewer assets={unit.assets} surveyId={props.surveyId} />
                                                    </div>
                                                </DataPanelRow>
                                            </>
                                        )}
                                    </SurveyDetailsUnitPanelDetails>

                                    {unit.manifestations.map((manifestation) => (
                                        <SurveyDetailsManifestationPanel
                                            // eslint-disable-next-line react/jsx-key
                                            extraColumns={[
                                                <Nen3140ScoreLabel
                                                    key="key"
                                                    nen3140Score={manifestation.surveyInspectionStandardData.nen3140Score}
                                                />,
                                            ]}
                                            key={manifestation.id}
                                            breakDownOpenState={breakDownOpenState}
                                            onChangeBreakdownOpenState={onChangeBreakdownOpenState}
                                            manifestation={manifestation}
                                        >
                                            <PanelResultHeader>{translate('common.assessment')}</PanelResultHeader>
                                            <strong>
                                                <SelectedOptionToText
                                                    selectedValue={manifestation.surveyInspectionStandardData.nen3140Score}
                                                    options={nen3140SelectOptions.nen3140Score}
                                                />
                                            </strong>
                                            {manifestation.surveyInspectionStandardData.remarks && (
                                                <>
                                                    <PanelResultHeader>{translate('common.remarks')}</PanelResultHeader>
                                                    <TextWithLineBreaks text={manifestation.surveyInspectionStandardData.remarks} />
                                                </>
                                            )}
                                            {manifestation.assets.length > 0 && (
                                                <>
                                                    <PanelResultHeader>{translate('nen3140.visualsAndDocuments')}</PanelResultHeader>
                                                    <DataPanelRow>
                                                        <div className="p10">
                                                            <AssetsViewer assets={manifestation.assets} surveyId={props.surveyId} />
                                                        </div>
                                                    </DataPanelRow>
                                                </>
                                            )}
                                        </SurveyDetailsManifestationPanel>
                                    ))}
                                </SurveyDetailsUnitPanel>
                            ))}
                        </SurveyDetailsElementPanel>
                    ))}
                </SurveyDetailsElementGroupReportPanel>
            </PanelBody>
        </PanelWithoutForm>
    );
}
